import { FCWithChildren } from '@types';
/* eslint-disable jsx-a11y/label-has-for */
import React, { ReactNode } from 'react';

import { useSuspenseQuery } from '@tanstack/react-query';
import Select from 'react-select';

import { getDeviceTypes } from 'sdk-apogee';

import { Controller, useFormContext } from 'react-hook-form';

import { MdDevicesOther } from 'react-icons/md';

import { HelperText } from '@components';
import { useThemeMode } from '@contexts/ThemeModeContext';
import { getReactSelectStyles } from '@utils';
import classNames from 'classnames';
import { StylesConfig } from 'react-select';

type DeviceTypeSelectProps = {
  isMobile: boolean;
  isModal?: boolean;
  deviceNameBuilder: (value: string) => void;
  grouped?: boolean;
};

const DeviceTypeSelect: FCWithChildren<DeviceTypeSelectProps> = ({
  isMobile,
  isModal,
  deviceNameBuilder,
  grouped = false,
}) => {
  const { resolvedTheme } = useThemeMode();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { data = [] } = useSuspenseQuery({
    queryKey: ['device-types'],
    queryFn: getDeviceTypes,
  });

  const options = data.map(({ id, name }) => ({ label: name, value: id }));
  const sortedOptions = options.sort((a, b) => a.label.localeCompare(b.label));
  const selectStyles = React.useMemo(() => {
    const baseStyles = getReactSelectStyles(resolvedTheme);

    if (!grouped) {
      return baseStyles;
    }

    const groupedStyles: StylesConfig<any, boolean> = {
      ...baseStyles,
      control: (base, state) => ({
        ...baseStyles.control?.(base, state),
        backgroundColor: 'transparent',
        border: 0,
        boxShadow: 'none',
        minHeight: 'auto',
        '&:hover': {
          border: 0,
        },
      }),
      valueContainer: (base) => ({
        ...base,
        padding: 0,
      }),
      input: (base, state) => ({
        ...(baseStyles.input?.(base, state) || base),
        margin: 0,
        padding: 0,
      }),
      singleValue: (base, state) => ({
        ...(baseStyles.singleValue?.(base, state) || base),
        color: resolvedTheme === 'dark' ? '#93c5fd' : '#0075da',
        margin: 0,
      }),
      placeholder: (base, state) => ({
        ...(baseStyles.placeholder?.(base, state) || base),
        color: resolvedTheme === 'dark' ? '#93c5fd' : '#0075da',
        margin: 0,
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      dropdownIndicator: (base, state) => ({
        ...(baseStyles.dropdownIndicator?.(base, state) || base),
        padding: 0,
      }),
    };

    return groupedStyles;
  }, [grouped, resolvedTheme]);

  return (
    <div
      className={classNames({ 'w-full my-2 mr-10 md:w-1/3 md:my-4': !isModal && !grouped })}
      style={{ zIndex: 2 }}
    >
      {!(isMobile || isModal || grouped) && (
        <MdDevicesOther className="text-header text-5xl text-primary mb-4" />
      )}
      {!grouped && (
        <label
          className="font-semibold text-sm md:text-base"
          htmlFor="choose-your-device"
          id="choose-your-device-label"
        >
          1. Choose Your Device
        </label>
      )}
      <Controller
        control={control}
        name="deviceType"
        render={({ field: { onChange, onBlur, value, ref } }) => (
          <Select
            placeholder="Select device type"
            className={classNames({ 'mt-4 pt-2 md:pt-0': !grouped })}
            styles={selectStyles}
            options={sortedOptions}
            onBlur={onBlur}
            onChange={(e) => {
              deviceNameBuilder((e as any).label);
              onChange(e);
            }}
            value={value}
            ref={ref}
            inputId="choose-your-device"
            aria-labelledby={grouped ? undefined : 'choose-your-device-label'}
          />
        )}
      />

      {!grouped && (
        <HelperText valid={!errors.deviceType?.message}>
          {errors.deviceType?.message as ReactNode}
        </HelperText>
      )}
    </div>
  );
};

export default DeviceTypeSelect;
